import type {
  CanvasComponentConfig,
  ReportCanvasLayout,
} from '@erp/shared-types';
import { reportDefaultDatasetKey } from './useWorkbookReportData';

/** A component reading its report's default dataset. */
function withoutDataset(
  component: CanvasComponentConfig,
): CanvasComponentConfig {
  const copy = { ...component };
  delete copy.datasetKey;
  return copy;
}

/**
 * What a library report of one Worksheet keeps of a Report Output: the
 * components charting that Worksheet, each reading the library report's one
 * dataset. Components charting other Worksheets bind columns it doesn't have,
 * so they are left out (the canvas closes the gaps they leave when it loads
 * the layout), as are rows left empty in a layout saved as rows.
 */
export function libraryReportLayout(
  layout: ReportCanvasLayout,
  definition: { sheets: readonly { id: string }[] },
  sheetId: string,
): ReportCanvasLayout {
  const defaultKey = reportDefaultDatasetKey(definition);
  const charts = (component: CanvasComponentConfig) =>
    (component.datasetKey ?? defaultKey) === sheetId;
  if ('items' in layout)
    return {
      items: layout.items
        .filter((item) => charts(item.component))
        .map((item) => ({
          ...item,
          component: withoutDataset(item.component),
        })),
    };
  return {
    data: layout.data
      .map((row) => row.filter(charts).map(withoutDataset))
      .filter((row) => row.length > 0),
  };
}
